import { useState } from "react";
import { toast } from "sonner";
import { CalendarOff, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { useClinics } from "@/hooks/useClinics";
import { api, formatApiError } from "@/lib/api";
import { LeaveResult } from "@/components/admin/LeaveResult";

const blank = (today) => ({ start_date: today, end_date: today, clinics: null, wholeDay: true, start_time: "10:00", end_time: "13:00", note: "" });

const ClinicChecks = ({ clinics, selected, toggle }) => (
  <div className="space-y-2">
    {clinics.map((c) => (
      <label key={c.id} className="flex items-center gap-3 text-sm cursor-pointer">
        <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`leave-clinic-${c.id}`} />
        {c.name}
      </label>
    ))}
  </div>
);

const Field = ({ label, children }) => <div className="space-y-2"><Label>{label}</Label>{children}</div>;

export const LeaveDialog = ({ today, onSaved }) => {
  const clinics = useClinics();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank(today));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const selected = form.clinics ?? clinics.map((c) => c.id);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (id) => setForm((f) => ({ ...f, clinics: selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id] }));

  const onOpenChange = (v) => { setOpen(v); if (v) { setForm(blank(today)); setResult(null); } };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected.length) return toast.error("Choose at least one clinic");
    if (form.end_date < form.start_date) return toast.error("'To' date must be on or after the 'From' date");
    if (!form.wholeDay && form.end_time <= form.start_time) return toast.error("'Until' time must be after the 'Blocked from' time");
    setLoading(true);
    try {
      const { wholeDay, start_time, end_time, ...rest } = form;
      const body = { ...rest, clinics: selected, ...(!wholeDay && { start_time, end_time }) };
      const { data } = await api.post("/admin/leaves", body);
      toast.success(`Leave saved${data.cancelled.length ? `, ${data.cancelled.length} appointment(s) cancelled` : ""}`);
      setResult(data.cancelled);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full" data-testid="leave-open-btn"><CalendarOff className="mr-1 h-4 w-4" /> Block days / leave</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="leave-dialog">
        <DialogHeader><DialogTitle>{result ? "Leave saved" : "Block days or hours"}</DialogTitle></DialogHeader>
        {result ? <LeaveResult cancelled={result} onDone={() => setOpen(false)} /> : (
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <Field label="From"><Input type="date" min={today} value={form.start_date} onChange={set("start_date")} required data-testid="leave-start-date" /></Field>
              <Field label="To"><Input type="date" min={form.start_date || today} value={form.end_date} onChange={set("end_date")} required data-testid="leave-end-date" /></Field>
            </div>
            <label className="flex items-center justify-between gap-3 text-sm font-medium">
              Whole day
              <Switch checked={form.wholeDay} onCheckedChange={(v) => setForm((f) => ({ ...f, wholeDay: v }))} data-testid="leave-whole-day-switch" />
            </label>
            {!form.wholeDay && (
              <div className="grid grid-cols-2 gap-4">
                <Field label="Blocked from"><Input type="time" step="900" value={form.start_time} onChange={set("start_time")} required data-testid="leave-start-time" /></Field>
                <Field label="Until"><Input type="time" step="900" value={form.end_time} onChange={set("end_time")} required data-testid="leave-end-time" /></Field>
              </div>
            )}
            <Field label="Clinics"><ClinicChecks clinics={clinics} selected={selected} toggle={toggle} /></Field>
            <Field label="Note (optional, staff only)"><Input value={form.note} maxLength={200} onChange={set("note")} placeholder="e.g. Conference in Delhi" data-testid="leave-note-input" /></Field>
            <p className="rounded-xl bg-red-50 text-red-700 text-sm px-4 py-3" data-testid="leave-warning">
              Patients can't book these times. Any appointments already booked in this period will be cancelled automatically, and patients who gave an email will be told by email.
            </p>
            <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="leave-submit-btn">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save leave
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
